import { Toast } from "../../utils/Toast";

type JobStatus = "queued" | "running" | "success" | "failed" | "missing";

type ReleaseJob = {
  job_id?: string;
  release_id?: string;
  status?: JobStatus | string;
  progress_percent?: number;
  current_step?: string;
  message?: string;
  lines?: string[];
  errors?: string[];
  artifact?: {
    relative_path?: string;
    relative_zip_path?: string;
    path?: string;
    zip_path?: string;
  };
  redirect?: string;
};

const FORM_SELECTOR = "form[data-source-protection-release-form]";
const STEPS = [
  "preflight",
  "npm-build",
  "package-copy",
  "php-protection",
  "js-obfuscation",
  "forbidden-scan",
  "manifest",
  "integrity",
  "zip",
  "completed",
];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

function normalizeJob(payload: unknown): ReleaseJob {
  const data = asRecord(payload);
  return asRecord(data.job) as ReleaseJob;
}

function setText(root: ParentNode, selector: string, text: string): void {
  const el = root.querySelector<HTMLElement>(selector);
  if (el) el.textContent = text;
}

function setProgress(root: ParentNode, percent: number): void {
  const safe = Math.max(0, Math.min(100, Math.round(percent)));
  const bar = root.querySelector<HTMLElement>("[data-source-protection-progress-bar]");
  if (bar) bar.style.width = `${safe}%`;
  setText(root, "[data-source-protection-progress-percent]", `${safe}%`);
}

function updateSteps(root: ParentNode, currentStep: string, status: string): void {
  const currentIndex = STEPS.indexOf(currentStep);
  root.querySelectorAll<HTMLElement>("[data-source-protection-step]").forEach((el) => {
    const step = el.getAttribute("data-source-protection-step") ?? "";
    const index = STEPS.indexOf(step);
    el.classList.toggle("is-active", step === currentStep && status !== "failed");
    el.classList.toggle("is-complete", index >= 0 && currentIndex >= 0 && index < currentIndex);
    el.classList.toggle("is-failed", status === "failed" && step === currentStep);
  });
}

function renderJob(progressPanel: HTMLElement, job: ReleaseJob): void {
  const percent = Number(job.progress_percent ?? 0);
  const status = String(job.status ?? "");
  const step = String(job.current_step ?? "queued");
  const message = String(job.message ?? "");
  const lines = Array.isArray(job.lines) ? job.lines : [];
  const errors = Array.isArray(job.errors) ? job.errors : [];
  const mergedLines = status === "failed" && errors.length > 0 ? [...lines, ...errors] : lines;

  progressPanel.hidden = false;
  progressPanel.classList.toggle("is-success", status === "success");
  progressPanel.classList.toggle("is-failed", status === "failed" || status === "missing");
  setProgress(progressPanel, percent);
  setText(progressPanel, "[data-source-protection-progress-step]", step);
  setText(progressPanel, "[data-source-protection-progress-message]", message);
  setText(progressPanel, "[data-source-protection-progress-log]", mergedLines.join("\n"));
  updateSteps(progressPanel, step, status);

  const log = progressPanel.querySelector<HTMLElement>("[data-source-protection-progress-log]");
  if (log) log.scrollTop = log.scrollHeight;

  if (status === "success") {
    const artifact = asRecord(job.artifact) as ReleaseJob["artifact"];
    const artifactPanel = progressPanel.querySelector<HTMLElement>("[data-source-protection-artifact]");
    if (artifactPanel) artifactPanel.hidden = false;
    setText(progressPanel, "[data-source-protection-artifact-path]", String(artifact?.relative_path ?? artifact?.path ?? ""));
    setText(progressPanel, "[data-source-protection-artifact-zip]", String(artifact?.relative_zip_path ?? artifact?.zip_path ?? ""));
    const detailLink = progressPanel.querySelector<HTMLAnchorElement>("[data-source-protection-detail-link]");
    if (detailLink && typeof job.redirect === "string" && job.redirect !== "") {
      detailLink.href = job.redirect;
    }
  }
}

async function fetchJson(url: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(init?.headers ?? {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const data = asRecord(payload);
    throw new Error(String(data.message ?? data.msg ?? "요청을 처리할 수 없습니다."));
  }
  return payload;
}

function bindReleaseForm(form: HTMLFormElement): void {
  if (form.dataset.sourceProtectionReleaseBound === "1") return;
  form.dataset.sourceProtectionReleaseBound = "1";

  const jobUrl = form.getAttribute("data-source-protection-job-url") ?? "";
  const statusTemplate = form.getAttribute("data-source-protection-job-status-url-template") ?? "";
  const progressPanel = document.querySelector<HTMLElement>("[data-source-protection-progress]");
  const submitButton = form.querySelector<HTMLButtonElement>("[data-source-protection-submit]");
  if (!jobUrl || !statusTemplate || !progressPanel) return;

  let controller: AbortController | null = null;
  let pollTimer = 0;
  let pollDelay = 700;

  const clearPoll = (): void => {
    if (pollTimer > 0) {
      window.clearTimeout(pollTimer);
      pollTimer = 0;
    }
  };

  const setSubmitting = (submitting: boolean): void => {
    submitButton?.toggleAttribute("disabled", submitting);
    form.classList.toggle("is-submitting", submitting);
  };

  const poll = async (jobId: string): Promise<void> => {
    clearPoll();
    const statusUrl = statusTemplate.replace("__JOB_ID__", encodeURIComponent(jobId));
    try {
      const payload = await fetchJson(statusUrl, { signal: controller?.signal });
      const job = normalizeJob(payload);
      renderJob(progressPanel, job);
      const status = String(job.status ?? "");
      if (status === "success") {
        setSubmitting(false);
        Toast.success("릴리즈 생성이 완료되었습니다.");
        return;
      }
      if (status === "failed" || status === "missing") {
        setSubmitting(false);
        Toast.error(String(job.message ?? "릴리즈 생성에 실패했습니다."));
        return;
      }
      pollDelay = 700;
      pollTimer = window.setTimeout(() => void poll(jobId), pollDelay);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      const message = error instanceof Error ? error.message : "진행 상태를 가져오지 못했습니다.";
      renderJob(progressPanel, {
        status: "running",
        progress_percent: 0,
        current_step: "network",
        message,
        lines: [message, "네트워크가 복구되면 자동으로 다시 확인합니다."],
      });
      pollDelay = Math.min(3000, pollDelay + 500);
      pollTimer = window.setTimeout(() => void poll(jobId), pollDelay);
    }
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    clearPoll();
    controller?.abort();
    controller = new AbortController();
    pollDelay = 700;
    setSubmitting(true);
    renderJob(progressPanel, {
      status: "queued",
      progress_percent: 0,
      current_step: "preflight",
      message: "작업을 생성하고 preflight를 준비합니다.",
      lines: ["release job: queued"],
    });

    void (async () => {
      try {
        const payload = await fetchJson(jobUrl, {
          method: "POST",
          body: new FormData(form),
          signal: controller?.signal,
        });
        const job = normalizeJob(payload);
        renderJob(progressPanel, job);
        const jobId = String(job.job_id ?? "");
        if (jobId === "") throw new Error("작업 ID를 받지 못했습니다.");
        if (String(job.status ?? "") === "failed") {
          setSubmitting(false);
          Toast.error(String(job.message ?? "릴리즈 작업을 시작하지 못했습니다."));
          return;
        }
        pollTimer = window.setTimeout(() => void poll(jobId), pollDelay);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        const message = error instanceof Error ? error.message : "릴리즈 작업을 시작하지 못했습니다.";
        renderJob(progressPanel, {
          status: "failed",
          progress_percent: 100,
          current_step: "failed",
          message,
          lines: [message],
          errors: [message],
        });
        setSubmitting(false);
        Toast.error(message);
      }
    })();
  });
}

export function initSourceProtectionRelease(): void {
  document.querySelectorAll<HTMLFormElement>(FORM_SELECTOR).forEach(bindReleaseForm);
}
